import { loadStripe } from '@stripe/stripe-js';
import {
  applyDiscount,
  validateDiscountCode,
  PAYPAL_CONFIG
} from './paymentService';
import {
  getEventById,
  createEvent,
  createEventRegistration,
  updateEventRegistration
} from './firestoreService';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Event pricing tiers and member discounts
export const EVENT_PRICING = {
  // Base pricing for different event types
  NETWORKING: {
    member: 25,
    nonMember: 50
  },
  WORKSHOP: {
    member: 75,
    nonMember: 150
  },
  CONFERENCE: {
    member: 200,
    nonMember: 400
  },
  VIRTUAL: {
    member: 15,
    nonMember: 30
  }
};

// Member tier discounts for events
export const MEMBER_DISCOUNTS = {
  'founding-member': 50, // 50% off
  'vip-member': 25,      // 25% off
  '5-year-member': 15,   // 15% off
  '10-year-member': 20,  // 20% off
  'ambassador': 10       // 10% off
};

// Calculate event pricing based on user status and badges
export const calculateEventPrice = (event, userProfile, registrationType = 'member') => {
  if (!event || !event.pricing) {
    return { finalPrice: 0, discountAmount: 0, breakdown: [] };
  }

  let basePrice = 0;
  const breakdown = [];

  // Determine base price - NO SPONSOR PRICING
  if (registrationType === 'nonMember') {
    basePrice = event.pricing.nonMember || 0;
    breakdown.push({ item: 'Non-Member Registration', amount: basePrice });
  } else {
    // Default to member pricing for all other cases (including sponsors)
    basePrice = event.pricing.member || 0;
    breakdown.push({ item: 'Member Registration', amount: basePrice });
  }

  let totalDiscount = 0;
  let finalPrice = basePrice;

  // Apply member badge discounts (only for members)
  if (registrationType === 'member' && userProfile?.badges) {
    let highestDiscount = 0;
    let discountSource = '';

    userProfile.badges.forEach(badge => {
      const discount = MEMBER_DISCOUNTS[badge.id];
      if (discount && discount > highestDiscount) {
        highestDiscount = discount;
        discountSource = badge.name;
      }
    });

    if (highestDiscount > 0) {
      const discountAmount = (basePrice * highestDiscount) / 100;
      totalDiscount += discountAmount;
      breakdown.push({ 
        item: `${discountSource} Discount (${highestDiscount}%)`, 
        amount: -discountAmount 
      });
    }
  }

  // Apply event-specific discounts
  if (event.discounts) {
    event.discounts.forEach(discount => {
      if (discount.active && new Date() <= new Date(discount.validUntil)) {
        const discountAmount = discount.type === 'percentage' 
          ? (basePrice * discount.value) / 100
          : discount.value;
        
        totalDiscount += discountAmount;
        breakdown.push({ 
          item: `${discount.name} (${discount.type === 'percentage' ? discount.value + '%' : '$' + discount.value})`, 
          amount: -discountAmount 
        });
      }
    });
  }

  finalPrice = Math.max(basePrice - totalDiscount, 0);

  return {
    basePrice,
    finalPrice,
    discountAmount: totalDiscount,
    breakdown,
    savings: totalDiscount
  };
};

// Create Stripe checkout session for event registration
export const createEventCheckoutSession = async (eventId, userId, registrationType, discountCode = null) => {
  try {
    // Use Firebase Cloud Function URL directly
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/createEventCheckoutSession';

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId: String(eventId), // Ensure eventId is a string for Firestore
        userId,
        registrationType,
        discountCode,
        successUrl: `${window.location.origin}/events/${eventId}/registration/success`,
        cancelUrl: `${window.location.origin}/events/${eventId}/registration/cancel`
      }),
    });

    const session = await response.json();
    
    if (session.error) {
      throw new Error(session.error);
    }

    return session;
  } catch (error) {
    console.error('Error creating event checkout session:', error);
    throw error;
  }
};

// Create PayPal order for event registration
export const createEventPayPalOrder = async (eventId, userId, registrationType, discountCode = null) => {
  try {
    // Call the Firebase function to create a real PayPal order for events
    const response = await fetch('https://us-central1-the-apn.cloudfunctions.net/createEventPayPalOrder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId,
        userId,
        registrationType,
        discountCode,
        successUrl: `${window.location.origin}/events/${eventId}/registration/success`,
        cancelUrl: `${window.location.origin}/events/${eventId}/registration/cancel`
      }),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to create PayPal order for event';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse the error response, use the status
        errorMessage = `PayPal service error (${response.status})`;
      }
      throw new Error(errorMessage);
    }

    const order = await response.json();
    console.log('PayPal event order created:', order);

    // PayPal buttons expect just the order ID
    return order.id;
  } catch (error) {
    console.error('Error creating event PayPal order:', error);
    throw error;
  }
};

// Handle successful event payment
export const handleEventPaymentSuccess = async (paymentData, eventId, userId, registrationType) => {
  try {
    // Create event registration record
    const registrationData = {
      eventId,
      userId,
      registrationType,
      status: 'confirmed',
      paymentStatus: 'paid',
      paymentMethod: paymentData.paymentMethod,
      amount: paymentData.amount,
      registrationDate: new Date(),
      paymentData: {
        transactionId: paymentData.transactionId,
        paymentMethod: paymentData.paymentMethod,
        timestamp: new Date()
      }
    };

    await createEventRegistration(registrationData);
    
    return {
      success: true,
      registrationData
    };
  } catch (error) {
    console.error('Error handling event payment success:', error);
    throw error;
  }
};

// Get event registration status
export const getEventRegistrationStatus = async (eventId, userId) => {
  try {
    // This would query the registrations collection
    // Implementation depends on your Firestore structure
    return {
      isRegistered: false,
      registrationType: null,
      paymentStatus: null
    };
  } catch (error) {
    console.error('Error getting registration status:', error);
    return { isRegistered: false };
  }
};

// Create event with automatic payment integration
export const createEventWithPaymentIntegration = async (eventData) => {
  try {
    // First create the event in Firestore
    const eventId = await createEvent(eventData);
    console.log('✅ Event created in Firestore with ID:', eventId);

    // Create Stripe products and prices for the event
    if (eventData.enablePayments && eventData.pricing) {
      console.log('🔄 Creating Stripe products for event...');
      try {
        const stripeProducts = await createStripeProductsForEvent(eventId, eventData);
        console.log('✅ Stripe products created:', stripeProducts);

        // Update the event with Stripe product IDs
        await updateEventWithStripeData(eventId, stripeProducts);
        console.log('✅ Event updated with Stripe data');
      } catch (stripeError) {
        console.warn('⚠️ Stripe integration failed, but event was created:', stripeError);
        // Don't fail the entire event creation if Stripe fails
        // The event can still be created without payment integration
      }
    }

    return eventId;
  } catch (error) {
    console.error('❌ Error creating event with payment integration:', error);
    throw error;
  }
};

// Create Stripe products and prices for an event
const createStripeProductsForEvent = async (eventId, eventData) => {
  try {
    // Use Firebase Cloud Function URL directly
    const functionUrl = 'https://us-central1-the-apn.cloudfunctions.net/createEventProducts';

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventId,
        eventTitle: eventData.title,
        eventDescription: eventData.description,
        pricing: eventData.pricing
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create Stripe products: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating Stripe products:', error);
    throw error;
  }
};

// Update event with Stripe product data
const updateEventWithStripeData = async (eventId, stripeProducts) => {
  try {
    const { updateDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    await updateDoc(doc(db, 'events', eventId), {
      stripeProducts,
      paymentIntegrationEnabled: true,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating event with Stripe data:', error);
    throw error;
  }
};

export default {
  EVENT_PRICING,
  MEMBER_DISCOUNTS,
  calculateEventPrice,
  createEventCheckoutSession,
  createEventPayPalOrder,
  handleEventPaymentSuccess,
  getEventRegistrationStatus,
  createEventWithPaymentIntegration
};
